// apps/client/src/components/reports/VarianceByDimensionTable.tsx
// Sortable per-dimension breakdown for /reports/approved-vs-settled.

import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { formatCurrencyShorthand } from '../../utils/formatters';
import type { VarianceByDimensionRow, VarianceGroupBy } from '../../hooks/reports/useApprovedVsSettledVariance';

type SortKey =
  | 'label'
  | 'loanCount'
  | 'shortfallLoans'
  | 'approvedTotalCents'
  | 'settledTotalCents'
  | 'upfrontActualCents'
  | 'upfrontImpliedFullCents'
  | 'upfrontDecreaseCents';

interface VarianceByDimensionTableProps {
  rows: VarianceByDimensionRow[];
  groupBy: VarianceGroupBy;
  /** Display-name lookup. Map keys are dim_key values returned by the callable. */
  labelLookup?: Record<string, string>;
}

const DIMENSION_HEADER: Record<VarianceGroupBy, string> = {
  template: 'Loan Book',
  entity: 'Loan Book',
  office: 'Office',
  broker: 'Broker',
};

export const VarianceByDimensionTable: React.FC<VarianceByDimensionTableProps> = ({
  rows,
  groupBy,
  labelLookup = {},
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('upfrontDecreaseCents');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const enrichedRows = useMemo(() => rows.map(r => ({
    ...r,
    displayLabel: r.key ? (labelLookup[r.key] || r.label) : '(unattributed)',
  })), [rows, labelLookup]);

  const sorted = useMemo(() => {
    const copy = [...enrichedRows];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'label') {
        cmp = a.displayLabel.localeCompare(b.displayLabel);
      } else {
        const av = (a as any)[sortKey] ?? 0;
        const bv = (b as any)[sortKey] ?? 0;
        cmp = av - bv;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [enrichedRows, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  };

  const SortIcon: React.FC<{ active: boolean; dir: 'asc' | 'desc' }> = ({ active, dir }) => {
    if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  if (rows.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <p className="text-gray-400 text-sm">No breakdown data available for the current filter.</p>
      </div>
    );
  }

  const headerCell = (label: string, key: SortKey, align: 'left' | 'right' = 'right') => (
    <th
      className={`py-2 px-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => toggleSort(key)}
    >
      <span className={`inline-flex items-center gap-1.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <SortIcon active={sortKey === key} dir={sortDir} />
      </span>
    </th>
  );

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/40">
            <tr>
              {headerCell(DIMENSION_HEADER[groupBy], 'label', 'left')}
              {headerCell('Loans', 'loanCount')}
              {headerCell('Short', 'shortfallLoans')}
              {headerCell('Approved', 'approvedTotalCents')}
              {headerCell('Settled', 'settledTotalCents')}
              {headerCell('Upfront paid', 'upfrontActualCents')}
              {headerCell('Upfront implied', 'upfrontImpliedFullCents')}
              {headerCell('Decrease', 'upfrontDecreaseCents')}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {sorted.map(row => (
              <tr key={row.key ?? '__null__'} className="hover:bg-gray-700/20 transition">
                <td className="py-2.5 px-3 text-gray-200 font-medium">{row.displayLabel}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{row.loanCount.toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{row.shortfallLoans.toLocaleString()}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(row.approvedTotalCents)}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(row.settledTotalCents)}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(row.upfrontActualCents)}</td>
                <td className="py-2.5 px-3 text-right text-gray-300 tabular-nums">{formatCurrencyShorthand(row.upfrontImpliedFullCents)}</td>
                <td className="py-2.5 px-3 text-right font-semibold tabular-nums" style={{
                  color: row.upfrontDecreaseCents > 0 ? '#f87171' : '#9ca3af'
                }}>
                  {formatCurrencyShorthand(row.upfrontDecreaseCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VarianceByDimensionTable;
